import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { Clock } from 'lucide-react';

export const CompanyTimezoneSetting = () => {
  const { t, i18n } = useTranslation();
  const [timezone, setTimezone] = useState('UTC');
  const [options, setOptions] = useState(['UTC']);
  const [isConfigured, setIsConfigured] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axiosInstance.get('/companies/timezone')
      .then(({ data }) => {
        setTimezone(data.timezone || 'UTC');
        setOptions(data.options || ['UTC']);
        setIsConfigured(!!data.isConfigured);
      })
      .catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axiosInstance.put('/companies/timezone', { timezone });
      setIsConfigured(true);
      toast.success(t('timezoneSetting.saved'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('timezoneSetting.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const localNow = (() => {
    try {
      return new Intl.DateTimeFormat(i18n.language || undefined, {
        timeZone: timezone, dateStyle: 'medium', timeStyle: 'short'
      }).format(new Date());
    } catch {
      return '';
    }
  })();

  return (
    <Card data-testid="company-timezone-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-blue-600" />
          {t('timezoneSetting.title')}
          {!isConfigured && (
            <Badge variant="outline" data-testid="timezone-fallback-badge">
              {t('timezoneSetting.usingUtcFallback')}
            </Badge>
          )}
        </CardTitle>
        <CardDescription>{t('timezoneSetting.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1">
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger data-testid="timezone-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {options.map((tz) => (
                  <SelectItem key={tz} value={tz} data-testid={`timezone-option-${tz}`}>
                    {tz.replace(/_/g, ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={save} disabled={saving} data-testid="save-timezone-btn">
            {saving ? t('timezoneSetting.saving') : t('timezoneSetting.save')}
          </Button>
        </div>
        {localNow && (
          <p className="text-sm text-gray-600" data-testid="timezone-local-time">
            {t('timezoneSetting.localTimeNow')}: {localNow}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default CompanyTimezoneSetting;
